import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
	getSiteLocaleSettings,
	updateSiteLocaleSettings,
} from "services/localisation";
import { showError } from "../../../../../redux/common/actions";

export const BASELINE = "en";

const useLanguageSettings = (siteId) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [available, setAvailable] = useState([BASELINE]);
	const [defaultLocale, setDefaultLocale] = useState(BASELINE);
	const [enabled, setEnabled] = useState([BASELINE]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		let active = true;

		(async () => {
			const settingsRes = await getSiteLocaleSettings(siteId);

			if (!active) return;

			if (settingsRes?.status) {
				// The settings payload carries the full shipped catalogue - the
				// separate locales endpoint is token-gated to the site's ENABLED
				// list inside a site app, which would hide the other choices.
				setAvailable(settingsRes.data.availableLocales ?? [BASELINE]);
				setDefaultLocale(settingsRes.data.defaultLocale ?? BASELINE);
				setEnabled(settingsRes.data.enabledLocales ?? [BASELINE]);
			} else {
				dispatch(showError(t("message.failedToLoadLanguageSettings")));
			}
			setLoading(false);
		})();

		return () => {
			active = false;
		};
	}, [siteId, dispatch, t]);



	// Every change persists immediately: optimistic update, revert on failure so
	// the UI never shows a state the server rejected.
	const persist = useCallback(
		async (nextEnabled, nextDefault) => {
			const previous = { enabled, defaultLocale };
			setEnabled(nextEnabled);
			setDefaultLocale(nextDefault);
			setSaving(true);

			const response = await updateSiteLocaleSettings(siteId, {
				defaultLocale: nextDefault,
				enabledLocales: [...new Set([...nextEnabled, BASELINE])],
			});

			// No success toast: changes save on every click, so the disabled-while-
			// saving state is the feedback; only failures need announcing.
			if (!response?.status) {
				setEnabled(previous.enabled);
				setDefaultLocale(previous.defaultLocale);
				dispatch(
					showError(
						typeof response?.data === "string"
							? response.data
							: t("message.failedToSaveLanguageSettings")
					)
				);
			}
			setSaving(false);
			return response;
		},
		[enabled, defaultLocale, siteId, dispatch, t]
	);

	const toggleEnabled = useCallback(
		(tag) => {
			if (tag === BASELINE) return; // English is the locked fallback baseline
			const nextEnabled = enabled.includes(tag)
				? enabled.filter((l) => l !== tag)
				: [...enabled, tag];
			const nextDefault = nextEnabled.includes(defaultLocale)
				? defaultLocale
				: BASELINE;
			persist(nextEnabled, nextDefault);
		},
		[enabled, defaultLocale, persist]
	);

	const changeDefault = useCallback(
		(tag) => persist(enabled, tag),
		[enabled, persist]
	);

	return {
		available,
		defaultLocale,
		changeDefault,
		enabled,
		toggleEnabled,
		loading,
		saving,
	};
};

export default useLanguageSettings;
